import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { map, Subscription, filter, combineLatest, switchMap, EMPTY } from 'rxjs';
import moment from 'moment-timezone';
import { TargetingAccountPreferencesApiModel } from '../../../../api/targeting/model/targeting-account-preferences.api.model';
import { BasicFilterApiModel } from '../../../../api/shared/models/basic-filter.api.model';
import { cloneDeep } from 'lodash';

import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { DataSelectDialogComponent } from '../../../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { JournodexGeographyModel } from '../../../../Common/Journodex/models/journodex-geography.model';
import { TargetingQuotaInfo } from '../../../../Common/TargetingShared/model/targeting.quota.info';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

import { AccountManagementService } from '../../services/account-management.service';
import { PreferencesRegionSelectDialogService } from '../../services/preferences-region-select-dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { TimeModel } from '../../../../Common/Forms/models/time.model';
import { MinuteModel } from '../../../../Common/Forms/models/minute.model';
import { meridiansEnum } from '../../../../Common/Forms/enums/meridians.enum';
import { TwelveHourModel } from '../../../../Common/Forms/models/twelve-hour.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { EmailAccountConnectorConfigApi } from '../../../../api/emailAccountConnector/models/email-account-connector-config.api';
import { EmailAccountConnectorUsage } from '../../models/email-account-connector-usage.model';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { gateCall } from '@Core/rxjs-utils';
import { ConfigService } from '@Core/Config/services/config.service';


@Component({
    selector: 'agpr-targeting-preferences',
    templateUrl: './targeting-preferences.component.html',
    styleUrls: ['./targeting-preferences.component.scss'],
    providers: [
        PreferencesRegionSelectDialogService
    ],
    standalone: false
})
export class TargetingPreferencesComponent implements OnDestroy, OnInit {
    readonly currentDate: Date = new Date();
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly emailAccountPrivacyLevels = EmailAccountConnectorPrivacyLevelEnum;
    readonly csvExportOptions: DropdownOptionModel[] = [
        {
            label: 'None',
            value: null
        },
        {
            label: '250',
            value: 250
        },
        {
            label: '1000',
            value: 1000
        },
        {
            label: '5000',
            value: 5000
        }
    ];

    @Input() hasMonitoring: boolean = false;

    public startTime: TimeModel;
    public nextResetDate: Date;
    public nextResetTime: TimeModel;

    isReadOnly: boolean = false;
    preferences: TargetingAccountPreferencesApiModel;
    originalQuotaResetDate: Date;
    quotaInfo: TargetingQuotaInfo;
    quotaStartDate: Date;
    displayCustomDateBanner: boolean = false;
    userPrefs: PreferencesModel;
    quota: number;
    showEmailAccountConnectorConfig: boolean = false;
    emailAccountConnectorConfig: EmailAccountConnectorConfigApi;
    cachedEmailAccountConnectorConfig: EmailAccountConnectorConfigApi;
    emailAccountConnectorUsage: EmailAccountConnectorUsage = {
        sharedUsage: 0,
        privateUsage: 0
    };

    emailAccountErrors: any = {
        shared: false,
        privatePerUser: false
    };

    subscriptions: Subscription[] = [];

    constructor(
        public accountManagementService: AccountManagementService,
        public journodexRegionsService: JournodexRegionsService,
        public regionSelectDialogService: PreferencesRegionSelectDialogService,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        private configurationService: ConfigService,
    ) { }

    ngOnInit(): void {
        this.subscriptions.push(
            this.accountManagementService.getReadOnly().subscribe(result => {
                this.isReadOnly = result;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe( pref => {
                this.userPrefs = pref;
            })
        );
        this.subscriptions.push(
            this.accountManagementService.getTargetingAccountPreferences().subscribe(prefs => {
                if (prefs) {
                    this.preferences = prefs;
                    if (this.hasMonitoring) {
                        this.preferences.mentionLinksEnabled = false;
                    }
                    this.quota = +this.preferences.baselineDistributions + +this.preferences.additionalDistributions - +this.preferences.usedDistributions;
                }
            }),
            combineLatest([
                this.accountManagementService.getSelectedAccount().pipe(
                    map(account => account.startDate)
                ),
                this.accountManagementService.getTargetingAccountQuotaInfo().pipe(
                    filter(x => !!x)
                )
            ]).subscribe(([accountStartDate, quota]) => {
                this.quotaInfo = quota;
                this.originalQuotaResetDate = this.convertUTCToUserTimeZone(quota.nextResetDate);
                this.quotaStartDate = this.convertUTCToUserTimeZone(quota.periodStartDate ?? accountStartDate?.toDate());
                this.nextResetDate = this.convertUTCToUserTimeZone(quota.customResetDate ?? quota.nextResetDate);
                this.setQuotaStartTime(this.quotaStartDate);
                this.setQuotaNextResetTime(this.nextResetDate);
                this.displayCustomDateBanner = !!quota.customResetDate;
            }),
            this.accountManagementService.getSelectedAccount().pipe(
                map(account => this.configurationService.accountHasAccessToThirdPartyPitchingOnEnvironment(account.id))
            ).pipe(
                switchMap((accountHasThirdPartyPitchingEnabled) => {
                    this.showEmailAccountConnectorConfig = accountHasThirdPartyPitchingEnabled;
                    if (accountHasThirdPartyPitchingEnabled) {
                        return this.accountManagementService.emailAccountConnectorConfig$;
                    }
                    return EMPTY;
                })
            ).subscribe(config => {
                this.emailAccountConnectorConfig = config;
                this.cachedEmailAccountConnectorConfig = cloneDeep(config);
                this.emailAccountErrors.shared = false;
                this.emailAccountErrors.privatePerUser = false;
            }),
            this.accountManagementService.emailAccountConnectorUsage$.subscribe(usage => this.emailAccountConnectorUsage = usage)
        );
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    openRegionBrowser(includeOrExclude: IncludeOrExclude) {
        this.regionSelectDialogService.prepareData(includeOrExclude, this.preferences.geography[includeOrExclude]);
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingPreferences.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'AMT.targeting.regionalRestrictions.browserMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions'
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        selectedRegions = [];
                    }

                    this.preferences.geography[includeOrExclude] = selectedRegions?.map(region => region.collectionId);

                    // HACK: pass by reference.. so the value never gets updated in the agpr-include-exclude-chips
                    const geo = this.preferences.geography;
                    this.preferences.geography = {
                        include: geo.include.slice(),
                        exclude: geo.exclude.slice()
                    } as BasicFilterApiModel<number>;
                }
            }
        };
        this.coreDialogService.open(DataSelectDialogComponent, io);
    }

    resetToSystemResetDate() {
        this.displayCustomDateBanner = false;
        this.quotaInfo.customResetDate = null;
        this.nextResetDate = this.originalQuotaResetDate;
        this.setQuotaNextResetTime(this.nextResetDate);
    }

    setQuotaStartTime(quotaStartDate: Date) {
        const startHour = quotaStartDate.getHours();
        const startMinute = quotaStartDate.getMinutes();
        this.startTime = new TimeModel(this.userPrefs.timeFormat, startHour as TwelveHourModel,
            startMinute as MinuteModel,  ((startHour >= 12) ? meridiansEnum.PM : meridiansEnum.AM), this.userPrefs.timeZone, 0);
    }

    setQuotaNextResetTime(nextResetDate: Date): void {
        const resetHour = nextResetDate.getHours();
        const resetMinute = nextResetDate.getMinutes();
        this.nextResetTime = new TimeModel(this.userPrefs.timeFormat, resetHour as TwelveHourModel,
            resetMinute as MinuteModel,  ((resetHour >= 12) ? meridiansEnum.PM : meridiansEnum.AM), this.userPrefs.timeZone, 0);
    }

    nextResetDateTimeChanged() {
        const dateTime = new Date(this.nextResetDate.getTime());
        this.updateNextResetTime(dateTime);
        this.quotaInfo.customResetDate = this.convertUTCToUserTimeZone(dateTime, true);
        if (this.quotaInfo.customResetDate === this.originalQuotaResetDate) {
            this.quotaInfo.customResetDate = null;
        }
        this.displayCustomDateBanner = !!this.quotaInfo.customResetDate;
    }

    updateNextResetTime(dateTime: Date): void {
        dateTime.setHours(this.nextResetTime.meridian === meridiansEnum.PM ? this.nextResetTime.hour + 12 : this.nextResetTime.hour);
        dateTime.setMinutes(this.nextResetTime.minute);
    }

    /**
     * Convert the utc time to user's timezone or vice versa
     * @param input the date to be converted
     * @param reverse flag to convert user's timezone to utc, default false
     * @returns date object containing the new time
     */
    convertUTCToUserTimeZone(input: Date, reverse = false): Date {
        const inputTime = moment(input?.getTime());
        const inputToUTC = inputTime.utcOffset();
        const userToUTC = inputTime.tz(this.userPrefs.timeZone).utcOffset();
        const offset = userToUTC - inputToUTC;
        if (!reverse){
            inputTime.add(offset, 'minutes');
        }else{
            inputTime.subtract(offset, 'minutes');
        }

        return inputTime.toDate();
    }

    checkError(privacyLevel: EmailAccountConnectorPrivacyLevelEnum): void {
        if (privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED) {
            this.emailAccountErrors.shared = false;
            if (this.emailAccountConnectorConfig.sharedConnectorLimit < this.emailAccountConnectorUsage.sharedUsage) {
                this.emailAccountErrors.shared = true;
                this.emailAccountConnectorConfig.sharedConnectorLimit = this.cachedEmailAccountConnectorConfig.sharedConnectorLimit;
            }
        } else {
            this.emailAccountErrors.privatePerUser = false;
            if (this.emailAccountConnectorConfig.privateConnectorLimit < this.emailAccountConnectorUsage.privateUsage) {
                this.emailAccountErrors.privatePerUser = true;
                this.emailAccountConnectorConfig.privateConnectorLimit = this.cachedEmailAccountConnectorConfig.privateConnectorLimit;
            }
        }
    }
}
